/* global dataLayer */

/* eslint-disable import/first */
import { useEffect } from 'react';
import NoSSR from 'react-no-ssr';
import Metrics from '@spotahome/soyuz-tracking';
import {
  clientErrorTracker,
  useSoyuzData,
  useSoyuzBrandConfig,
  trans
} from '@spotahome/soyuz/client';

import {
  ThemeProvider,
  CookiesProvider,
  ToastContainer,
  GoogleOneTap,
  UserInfoProvider,
  generateFontPreload,
  TenantTypeProvider,
  AppBanner,
  CitiesProvider,
  Footer,
  DeviceProvider,
  LoginSignupModalWrapper
} from '@spotahome/ui-library';
import { useCoreWebVitals } from '@spotahome/ui-library/src/hooks/useCoreWebVitals';
import usePageLoadTime from '@spotahome/ui-library/src/hooks/usePageLoadTime';
import OurGuarantee from '@spotahome/ui-library/src/tenant/components/OurGuarantee';
import FAQSection from '@spotahome/ui-library/src/tenant/components/FAQSection';
import { getCitiesQuery } from '@spotahome/ui-library/src/tenant/queries/graphql/cities';
import {
  LANDING_SECTIONS,
  TOP_CITIES,
  SEO_LINK_CITIES_KEYS
} from '@spotahome/ui-library/src/tenant/components/utils/seoConstants';

import getGqlClient from '@spotahome/landings-common/src/graphql/client';
import { sendTenantActionEvent } from '@spotahome/landings-common/src/graphql/mutations/tenantActions';
import PropertyTypeSection from '@spotahome/landings-common/src/components/PropertyTypeSection';
import CitiesShowcase from '@spotahome/landings-common/src/components/CitiesShowcase';
import TestimonialsCarousel from '@spotahome/landings-common/src/components/TestimonialsCarousel/TestimonialsCarousel';
import OptInWrapper from '@spotahome/landings-common/src/components/OptInWrapper';

import {
  getTenantFaq,
  getTenantStructuredFaq
} from '@spotahome/landings-common/src/utils/faq';

import FindYourHome from '@spotahome/ui-library/src/tenant/components/FindYourHome';

import {
  CustomDimensionsManager,
  constants
} from '@spotahome/ecommerce-tracking';

import '@spotahome/landings-common/src/scss/init.scss';

import ZohoChatbot from '@spotahome/ui-library/src/tenant/components/ZohoChatbot/ZohoChatbot';

import Hero from './components/Hero';
import GuaranteeSection from './components/GuaranteeSection';
import LandlordSection from './components/LandlordSection';

import './main.scss';

const getPreloadImageLink = imageUrl => {
  if (!imageUrl) return null;

  return {
    rel: 'preload',
    as: 'image',
    href: imageUrl
  };
};

const EXPERIMENT_IMAGES = [
  'https://sah-public-assets.s3.eu-west-1.amazonaws.com/images/home-monuments-redesign-mobile-white.svg'
];

const IMAGES_TO_PRELOAD = {
  smartphone: EXPERIMENT_IMAGES,
  tablet: EXPERIMENT_IMAGES,
  desktop: EXPERIMENT_IMAGES
};

const LandingPage = () => {
  const { [LandingPage.NAME]: requestedData } = useSoyuzData();
  const { isPartner } = useSoyuzBrandConfig();
  const {
    userId,
    initialCookie,
    isSafari,
    deviceType,
    deviceViewType,
    carouselCities,
    listCities
  } = requestedData;

  useCoreWebVitals();
  usePageLoadTime();

  useEffect(() => {
    Metrics.actions.configureClient(async ({ actionId, payload }) => {
      try {
        await sendTenantActionEvent(getGqlClient())({
          actionId,
          payload
        });
      } catch (e) {
        clientErrorTracker.error({
          error: e,
          source: `LandingPage-${actionId}`
        });
      }
    });
  }, []);

  useEffect(() => {
    if (userId && typeof dataLayer !== 'undefined') {
      dataLayer.push({ userId });
    }
  }, [userId]);

  return (
    <CookiesProvider initialCookie={initialCookie}>
      <ThemeProvider flags={{ newButtons: true }}>
        <UserInfoProvider>
          <DeviceProvider deviceType={deviceType}>
            <CitiesProvider skipAllCities>
              <ZohoChatbot />
              <AppBanner source="home" deviceType={deviceType} />
              <ToastContainer />
              {!userId && <GoogleOneTap />}
              <TenantTypeProvider>
                <div data-browser={isSafari ? 'safari' : 'other'}>
                  <Hero deviceType={deviceType} />
                  {!isPartner && (
                    <>
                      <GuaranteeSection />
                      <TestimonialsCarousel />
                      <OurGuarantee />
                      <PropertyTypeSection deviceType={deviceType} />
                      <LandlordSection />
                      <CitiesShowcase
                        deviceType={deviceType}
                        carouselCities={carouselCities}
                        listCities={listCities}
                      />
                      <FAQSection questions={getTenantFaq()} theme="homepage" />
                      <FindYourHome />
                      <Footer theme="beige" />
                      <OptInWrapper />
                    </>
                  )}
                  <NoSSR>
                    <LoginSignupModalWrapper deviceViewType={deviceViewType} />
                  </NoSSR>
                </div>
              </TenantTypeProvider>
            </CitiesProvider>
          </DeviceProvider>
        </UserInfoProvider>
      </ThemeProvider>
    </CookiesProvider>
  );
};

LandingPage.requestAllData = async (req, _, { graphqlClient }) => {
  const imagesToPreload = (
    IMAGES_TO_PRELOAD[req.device.type] || IMAGES_TO_PRELOAD.desktop
  ).map(i => getPreloadImageLink(i));
  const fontPreload = generateFontPreload(req.brandConfig);

  let carouselCities = [];
  let listCities = [];

  try {
    const citiesResponse = await getCitiesQuery(graphqlClient)(req.locale);
    const cities = citiesResponse.data.cities;
    const topCitiesKeys = Object.keys(TOP_CITIES);
    carouselCities = topCitiesKeys
      .map(key => cities.find(city => city.id === key))
      .map(city => ({
        id: city.id,
        cityName: city.cityName,
        country: {
          countryId: city.country.countryId,
          countryIsoCode: city.country.countryIsoCode
        }
      }))
      .filter(Boolean);

    listCities = cities
      .filter(
        city =>
          !topCitiesKeys.includes(city.id) &&
          SEO_LINK_CITIES_KEYS.includes(city.id)
      )
      .sort((a, b) => a.cityName.localeCompare(b.cityName))
      .map(city => ({
        id: city.id,
        cityName: city.cityName
      }));
  } catch (error) {
    req.errorTracker.error({
      error,
      source: 'LandingPage-getCities',
      locale: req.locale
    });
  }

  try {
    const { locale, userId } = req;
    const cdManager = CustomDimensionsManager();
    cdManager.setCommonCustomDimensions({
      userId,
      locale,
      pageType: constants.TRACKING_PAGE_TYPES.HOMEPAGE
    });

    const isSafari =
      req.device.info && req.device.info.client
        ? req.device.info.client.name.toLowerCase().includes('safari')
        : false;

    return {
      headers: {
        scripts: [
          { innerHTML: cdManager.flush() },
          {
            type: 'application/ld+json',
            innerHTML: JSON.stringify(getTenantStructuredFaq(req.trans))
          }
        ],
        links: [...fontPreload, ...imagesToPreload]
      },
      data: {
        userId: req.userId,
        deviceType: req.device.type,
        deviceViewType: req.device.viewType,
        initialCookie: req.headers.cookie,
        isSafari,
        carouselCities,
        listCities
      }
    };
  } catch (err) {
    req.logger.error('Landing page error', { err });
  }

  return {
    headers: {
      links: [...fontPreload, ...imagesToPreload]
    },
    data: {
      userId: null,
      topHomecards: null
    }
  };
};

LandingPage.NAME = 'LandingPage';

export default LandingPage;
